import { useState, useCallback, useRef, useEffect } from 'react'

/**
 * A hook that manages a value that auto-resets after `duration` ms.
 *
 * Returns [value, trigger, reset] where:
 *   - `trigger(val?)` sets value (defaults to `true`) and schedules auto-reset after `duration` ms.
 *   - `reset()` immediately resets to `initialValue` and cancels any pending timer.
 */
export function useTemporaryState<T>(
  initialValue: T,
  duration: number,
): [T, (value?: any) => void, () => void] {
  const [value, setValue] = useState<T>(initialValue)
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const trigger = useCallback(
    (triggerValue?: any) => {
      if (timerRef.current) clearTimeout(timerRef.current)
      setValue(triggerValue !== undefined ? triggerValue : (true as any))
      timerRef.current = setTimeout(() => setValue(initialValue), duration)
    },
    [initialValue, duration],
  )

  const reset = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    setValue(initialValue)
  }, [initialValue])

  return [value, trigger, reset]
}

export default useTemporaryState
